import React, { useEffect, useMemo, useState } from "react";
import "./createPuzzle.css";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import jigsawImage from "../../img/jigsaw-image.jpg";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { doc, setDoc } from "firebase/firestore";
import { database } from "../../database/firebase";

const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";
const TOTAL_USER_COUNT_KEY = "mythFactTotalUserCount";
const GRID_OPTIONS = ["3x3", "4x4", "5x5", "6x6", "7x7", "8x8"];
const TOTAL_TIME_MIN_SEC = 180;
const TOTAL_TIME_MAX_SEC = 600;
const VOTING_START_MIN_SEC = 60;
const VOTING_DURATION_MIN_SEC = 30;
const VOTING_DURATION_MAX_SEC = 120;
const VALID_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const REQUIRED_PUZZLE_DIM = 550;

async function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function randomFourDigitLobbyPinString() {
  return String(Math.floor(1000 + Math.random() * 9000));
}

function normalizeTotalUserCount(value) {
  const n = Number(value);
  if (n === 3 || n === 4) return n;
  return 4;
}

function parseMmSsToSeconds(value, fallbackSeconds) {
  const text = String(value || "").trim();
  const match = text.match(/^(\d{1,2}):(\d{1,2})$/);
  if (!match) return fallbackSeconds;
  const mm = Math.max(0, Math.min(99, Number(match[1]) || 0));
  const ss = Math.max(0, Math.min(59, Number(match[2]) || 0));
  return mm * 60 + ss;
}

function formatSecondsToMmSs(totalSeconds) {
  const safe = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const mm = Math.floor(safe / 60);
  const ss = safe % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

function CreatePuzzle() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data: themeData } = useSelector((state) => state.theme);
  const userData = useMemo(() => readStoredUserData(), []);
  const selectedThemeId =
    currentTheme ||
    String(userData?.themename || userData?.themeName || "").trim() ||
    null;

  const totalUserCount = useMemo(() => {
    const fromState = location.state?.totalUserCount ?? location.state?.playerCount;
    if (fromState != null) return normalizeTotalUserCount(fromState);
    try {
      const fromSession = sessionStorage.getItem(TOTAL_USER_COUNT_KEY);
      return normalizeTotalUserCount(fromSession);
    } catch {
      return 4;
    }
  }, [location.state]);

  const [gridSize, setGridSize] = useState("6x6");
  const [totalTimeSec, setTotalTimeSec] = useState(TOTAL_TIME_MAX_SEC);
  const [votingStartSec, setVotingStartSec] = useState(480);
  const [votingDurationSec, setVotingDurationSec] = useState(60);
  const [selectedPuzzleImage, setSelectedPuzzleImage] = useState("");
  const [creatingLobby, setCreatingLobby] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("idle");

  useEffect(() => {
    if (!selectedThemeId || !adminToken) return;
    dispatch(fetchThemeData({ themeId: selectedThemeId }));
  }, [adminToken, dispatch, selectedThemeId]);

  useEffect(() => {
    if (!themeData) return;
    const gridFromTheme = String(themeData.grid_size || "").trim();
    setGridSize(GRID_OPTIONS.includes(gridFromTheme) ? gridFromTheme : "6x6");
    const incomingTotal = parseMmSsToSeconds(themeData.total_time, TOTAL_TIME_MAX_SEC);
    const clampedTotal = Math.max(TOTAL_TIME_MIN_SEC, Math.min(TOTAL_TIME_MAX_SEC, incomingTotal));
    setTotalTimeSec(clampedTotal);

    const incomingStart = parseMmSsToSeconds(themeData.voting_start_time, 480);
    const clampedStart = Math.max(VOTING_START_MIN_SEC, Math.min(clampedTotal, incomingStart));
    setVotingStartSec(clampedStart);

    const incomingDuration = parseMmSsToSeconds(themeData.voting_duration, 60);
    const clampedDuration = Math.max(
      VOTING_DURATION_MIN_SEC,
      Math.min(VOTING_DURATION_MAX_SEC, incomingDuration)
    );
    setVotingDurationSec(clampedDuration);

    const images = Array.isArray(themeData.puzzle_images) ? themeData.puzzle_images : [];
    if (images.length > 0) {
      const randomIdx = Math.floor(Math.random() * images.length);
      setSelectedPuzzleImage(String(images[randomIdx] || "").trim());
    } else {
      setSelectedPuzzleImage("");
    }
  }, [themeData]);

  useEffect(() => {
    setVotingStartSec((prev) => Math.max(VOTING_START_MIN_SEC, Math.min(totalTimeSec, prev)));
  }, [totalTimeSec]);

  useEffect(() => {
    return () => {
      if (uploadPreviewUrl) {
        URL.revokeObjectURL(uploadPreviewUrl);
      }
    };
  }, [uploadPreviewUrl]);

  const displayImageSrc = uploadPreviewUrl
    ? uploadPreviewUrl
    : selectedPuzzleImage
    ? `${process.env.REACT_APP_S3_PATH}${selectedPuzzleImage}`
    : jigsawImage;

  const handleUploadFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError("");
    setUploadStatus("idle");

    if (!VALID_IMAGE_TYPES.includes(file.type)) {
      setUploadError("Only JPG/JPEG/PNG images are allowed.");
      event.target.value = "";
      return;
    }
    if (file.size >= MAX_IMAGE_BYTES) {
      setUploadError("Image size must be below 2MB.");
      event.target.value = "";
      return;
    }

    try {
      const { width, height } = await getImageDimensions(file);
      if (width !== REQUIRED_PUZZLE_DIM || height !== REQUIRED_PUZZLE_DIM) {
        setUploadError(
          `Image must be exactly ${REQUIRED_PUZZLE_DIM}x${REQUIRED_PUZZLE_DIM}.`
        );
        event.target.value = "";
        return;
      }
      if (uploadPreviewUrl) {
        URL.revokeObjectURL(uploadPreviewUrl);
      }
      setUploadFile(file);
      setUploadPreviewUrl(URL.createObjectURL(file));
    } catch {
      setUploadError("Unable to read image dimensions.");
      event.target.value = "";
    }
  };

  const handleUploadImage = async () => {
    if (!uploadFile) {
      setUploadError("Please choose an image first.");
      return;
    }
    if (!selectedThemeId || !adminToken) {
      setUploadError("Theme is not ready yet. Please try again.");
      return;
    }

    setUploading(true);
    setUploadError("");
    setUploadStatus("uploading");
    try {
      const formData = new FormData();
      formData.append("themeName", selectedThemeId);
      formData.append("puzzleImage", uploadFile);
      formData.append(
        "extraData",
        JSON.stringify({ mode: "puzzle_image_upload" })
      );

      const uploadResp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/updateThemeMedia`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
          body: formData,
        }
      );

      if (!uploadResp.ok) {
        const err = await uploadResp.json().catch(() => ({}));
        throw new Error(err.error || "Failed to upload image.");
      }

      const uploadData = await uploadResp.json();
      const uploadedFilename = uploadData?.uploadedFiles?.puzzleImage;
      if (!uploadedFilename) {
        throw new Error("Upload response missing filename.");
      }
      setSelectedPuzzleImage(String(uploadedFilename));
      setUploadStatus("success");
      Swal.fire({
        icon: "success",
        title: "Image uploaded",
        text: "Your puzzle image is ready.",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      setUploadError(error?.message || "Image upload failed.");
      setUploadStatus("error");
    } finally {
      setUploading(false);
    }
  };

  const handleStartGame = async () => {
    const sessionId = String(userData?.sessionId || "").trim();
    const userId = String(userData?.userId || userData?.userid || userData?.id || "").trim();
    if (!sessionId || !userId) {
      return;
    }

    setCreatingLobby(true);
    const lobbyName = randomFourDigitLobbyPinString();
    try {
      const lobbyRef = doc(database, `lobbies/${sessionId}/lobby/${lobbyName}`);
      await setDoc(
        lobbyRef,
        {
          totalUserCount,
          status: "waiting",
          createdBy: userId,
          createdAt: Date.now(),
          grid_size: gridSize,
          total_time: formatSecondsToMmSs(totalTimeSec),
          voting_start_time: formatSecondsToMmSs(votingStartSec),
          voting_duration: formatSecondsToMmSs(votingDurationSec),
          puzzle_image: selectedPuzzleImage || "",
        },
        { merge: true }
      );

      try {
        sessionStorage.setItem(CURRENT_LOBBY_KEY, lobbyName);
        sessionStorage.setItem(TOTAL_USER_COUNT_KEY, String(totalUserCount));
        const merged = { ...(userData || {}), lobby: lobbyName };
        const payload = JSON.stringify(merged);
        localStorage.setItem("userData", payload);
        sessionStorage.setItem("userData", payload);
      } catch {
        // ignore storage failures
      }

      navigate("/host", {
        state: {
          playerCount: totalUserCount,
          totalUserCount,
          loginPin: Number.parseInt(lobbyName, 10),
          lobbyName,
          grid_size: gridSize,
          total_time: formatSecondsToMmSs(totalTimeSec),
          voting_start_time: formatSecondsToMmSs(votingStartSec),
          voting_duration: formatSecondsToMmSs(votingDurationSec),
          puzzle_image: selectedPuzzleImage || "",
        },
      });
    } finally {
      setCreatingLobby(false);
    }
  };

  return (
    <div className="create-puzzle-page">
      <div className="create-puzzle-layout">
        <section className="create-puzzle-left-card">
          <h1 className="create-puzzle-main-title">Create or Generate Puzzle</h1>

          <div className="create-puzzle-image-panel create-puzzle-upload-panel">
            <h2 className="create-puzzle-section-title">Select Puzzle Image</h2>
            <p className="create-puzzle-upload-note">
              JPG / PNG, exact {REQUIRED_PUZZLE_DIM}x{REQUIRED_PUZZLE_DIM}, max 2MB.
            </p>
            <div className="create-puzzle-upload-row">
              <input
                id="create-puzzle-file-input"
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleUploadFileChange}
                disabled={uploading}
                className="create-puzzle-file-input"
              />
              <label
                htmlFor="create-puzzle-file-input"
                className={`create-puzzle-choose-btn${uploading ? " is-disabled" : ""}`}
              >
                Choose Image
              </label>
              <span
                className="create-puzzle-file-name"
                title={uploadFile?.name || ""}
              >
                {uploadFile?.name || "No file selected"}
              </span>
              <button
                type="button"
                className="create-puzzle-upload-btn"
                onClick={handleUploadImage}
                disabled={!uploadFile || uploading || !selectedThemeId || !adminToken}
              >
                {uploading ? "Uploading..." : "Upload Image"}
              </button>
            </div>
            {uploadStatus === "uploading" ? (
              <p className="create-puzzle-upload-info">
                <span className="create-puzzle-upload-spinner" aria-hidden="true" />
                Uploading image, please wait...
              </p>
            ) : uploadStatus === "success" ? (
              <p className="create-puzzle-upload-success">Image uploaded successfully.</p>
            ) : uploadError ? (
              <p className="create-puzzle-upload-error">{uploadError}</p>
            ) : null}
          </div>

          <div className="create-puzzle-image-panel">
            <h2 className="create-puzzle-section-title">Puzzle Preview</h2>
            <div className="create-puzzle-image-wrap">
              <img src={displayImageSrc} alt="Puzzle reference" className="create-puzzle-image" />
            </div>
          </div>
        </section>

        <section className="create-puzzle-right-card">
          <h2 className="create-puzzle-right-title">Game Settings</h2>

          <div className="create-puzzle-settings-box">
            <p className="create-puzzle-settings-label">Grid Selection</p>
            <div className="create-puzzle-grid-buttons">
              {GRID_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={gridSize === opt ? "is-active" : ""}
                  onClick={() => setGridSize(opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div className="create-puzzle-settings-box">
            <p className="create-puzzle-settings-label">Time Controls</p>

            <div className="create-puzzle-slider-row">
              <div className="create-puzzle-slider-label">
                <span>Total Time</span>
                <span>{formatSecondsToMmSs(totalTimeSec)} Minutes</span>
              </div>
              <input
                type="range"
                min={TOTAL_TIME_MIN_SEC}
                max={TOTAL_TIME_MAX_SEC}
                value={totalTimeSec}
                onChange={(e) => setTotalTimeSec(Number(e.target.value))}
              />
            </div>

            <div className="create-puzzle-slider-row">
              <div className="create-puzzle-slider-label">
                <span>Voting Start Time</span>
                <span>{formatSecondsToMmSs(votingStartSec)} Minutes</span>
              </div>
              <input
                type="range"
                min={VOTING_START_MIN_SEC}
                max={totalTimeSec}
                value={votingStartSec}
                onChange={(e) =>
                  setVotingStartSec(
                    Math.max(VOTING_START_MIN_SEC, Math.min(totalTimeSec, Number(e.target.value)))
                  )
                }
              />
            </div>

            <div className="create-puzzle-slider-row">
              <div className="create-puzzle-slider-label">
                <span>Voting Duration</span>
                <span>{formatSecondsToMmSs(votingDurationSec)} Minutes</span>
              </div>
              <input
                type="range"
                min={VOTING_DURATION_MIN_SEC}
                max={VOTING_DURATION_MAX_SEC}
                value={votingDurationSec}
                onChange={(e) => setVotingDurationSec(Number(e.target.value))}
              />
            </div>
          </div>
        </section>
      </div>

      <button type="button" className="create-puzzle-start-btn" onClick={handleStartGame} disabled={creatingLobby}>
        {creatingLobby ? "Starting..." : "Create Lobby"}
      </button>
    </div>
  );
}

export default CreatePuzzle;
